import { getAuthInfoFromLocalStorage } from "../context/AuthContext";
import { getLiteUrl } from "../utils";

export interface IQrCodeInfo {
  expire: number; // unix timestamp in seconds
  server: string;
  token: string;
  ts: number; // unix timestamp in seconds
  type: string;
  seatType?: string;
}

/**
 * Request a QR code for logging into the Lite Portal (my.firewalla.net).
 *
 * Mirrors firewalla.web entrance's `fetchBoxManageQRCode`, which calls the
 * Primary client's `/v1/sandbox/prepare` on the Lite Portal origin using the
 * access token (not the id token used by the api.firewalla.net endpoints).
 */
export async function fetchBoxManageQRCode(): Promise<IQrCodeInfo> {
  const authInfo = getAuthInfoFromLocalStorage();
  const response = await fetch(`${getLiteUrl()}/v1/sandbox/prepare`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Cache-Control": "no-cache",
      Authorization: `Bearer ${authInfo?.access_token}`,
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to prepare QR code. (code: ${response.status})`);
  }
  return response.json();
}

/**
 * Build the string encoded into the QR code, matching firewalla.web:
 * drop the `ts` and `seatType` fields, keep the rest.
 */
export function buildQrCodeString(info: IQrCodeInfo): string {
  const { ts, seatType, ...rest } = info;
  return JSON.stringify(rest);
}

export interface IQrCodeStatus {
  from: string;
  status: "scanned" | "box_added";
}

/**
 * Poll the login status of a prepared QR code, mirroring firewalla.web
 * entrance's `fetchQRCodeStatus`: `GET {LiteUrl}/v1/auth/{token}`.
 */
export async function fetchQRCodeStatus(token: string): Promise<IQrCodeStatus> {
  const authInfo = getAuthInfoFromLocalStorage();
  const response = await fetch(`${getLiteUrl()}/v1/auth/${token}`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Cache-Control": "no-cache",
      Authorization: `Bearer ${authInfo?.access_token}`,
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch QR code status. (code: ${response.status})`);
  }
  return response.json();
}
